/**
 * Besper SDK Loader - Mode-Aware Script Loading
 * 
 * Ensures SDK scripts are loaded deterministically by (botId, mode) to avoid
 * collisions between widget and container modes, and supports multiple instances.
 */

import { buildSdkUrl } from './sdkUrlHelper';
import logger from './logger';

/**
 * Normalize baseUrl for SDK (SDK adds /api itself, so strip it if present)
 */
const normalizeBaseUrlForSdk = (baseUrl) => {
  if (!baseUrl) return baseUrl;
  
  // Remove trailing /api if present (SDK will add it)
  if (baseUrl.endsWith('/api')) {
    return baseUrl.slice(0, -4);
  }
  return baseUrl;
};

/**
 * Find existing script by exact mode (not just botId)
 * @param {string} botId - Bot ID
 * @param {string} mode - SDK mode ('widget' or 'container')
 * @returns {HTMLScriptElement|null} - Existing script element or null
 */
const findScriptByMode = (botId, mode) => {
  const modePattern = `/api/v1/sdk/bots/${botId}/${mode}`;
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  return scripts.find(script => script.src.includes(modePattern)) || null;
};

/**
 * Get expected custom element name for mode
 * @param {string} mode - SDK mode
 * @returns {string} - Custom element name
 */
const getCustomElementName = (mode) => {
  if (mode === 'widget') return 'besper-bot-widget';
  if (mode === 'container') return 'besper-bot-container';
  throw new Error(`Unknown SDK mode: ${mode}`);
};

/**
 * Ensure SDK mode is loaded and custom element is registered
 * @param {Object} options - Configuration
 * @param {string} options.baseUrl - Base API URL
 * @param {string} options.botId - Bot ID
 * @param {string} options.mode - SDK mode ('widget' or 'container')
 * @param {string} [options.sidebarDefault] - Sidebar default state
 * @param {string} [options.containerId] - Container ID (for container mode)
 * @param {string} [options.apiEndpoint] - API endpoint override
 * @returns {Promise<void>} - Resolves when custom element is registered
 */
export const ensureSdkModeLoaded = async ({
  baseUrl,
  botId,
  mode,
  sidebarDefault,
  containerId,
  apiEndpoint
}) => {
  if (!baseUrl || !botId || !mode) {
    throw new Error('ensureSdkModeLoaded requires baseUrl, botId, and mode');
  }

  const customElementName = getCustomElementName(mode);

  // Check if custom element is already registered
  if (customElements.get(customElementName)) {
    logger.debug('SDK-LOADER', `SDK ${mode} mode already registered`, { customElementName });
    return;
  }

  // Check if script for this exact mode is already loaded/loading
  const existingScript = findScriptByMode(botId, mode);
  
  if (existingScript) {
    logger.debug('SDK-LOADER', `SDK ${mode} script already exists, waiting for registration`);
    
    // Wait for script to load and register custom element
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Timeout: ${customElementName} not registered after 10 seconds`));
      }, 10000);

      const checkRegistration = () => {
        if (customElements.get(customElementName)) {
          clearTimeout(timeout);
          logger.debug('SDK-LOADER', `${customElementName} registered`);
          resolve();
        }
      };

      // If script already loaded, check immediately
      if (existingScript.readyState === 'complete' || existingScript.readyState === 'loaded') {
        // Give it a moment to execute
        setTimeout(() => {
          checkRegistration();
          if (!customElements.get(customElementName)) {
            // Still not registered, poll for a bit
            let attempts = 0;
            const pollInterval = setInterval(() => {
              checkRegistration();
              if (attempts++ >= 50) { // 5 seconds
                clearInterval(pollInterval);
                clearTimeout(timeout);
                reject(new Error(`Script loaded but ${customElementName} not registered after 5 seconds`));
              }
            }, 100);
          }
        }, 100);
      } else {
        // Script is loading, wait for load event
        existingScript.addEventListener('load', () => {
          setTimeout(() => {
            checkRegistration();
            if (!customElements.get(customElementName)) {
              // Still not registered, poll for a bit
              let attempts = 0;
              const pollInterval = setInterval(() => {
                checkRegistration();
                if (attempts++ >= 50) { // 5 seconds
                  clearInterval(pollInterval);
                  clearTimeout(timeout);
                  reject(new Error(`Script loaded but ${customElementName} not registered after 5 seconds`));
                }
              }, 100);
            }
          }, 100);
        });
        existingScript.addEventListener('error', () => {
          clearTimeout(timeout);
          reject(new Error(`Failed to load SDK ${mode} script`));
        });
      }
    });
  }

  // Script doesn't exist, create and load it
  logger.info('SDK-LOADER', `Loading SDK ${mode} script for bot`, { botId });
  
  // Build script URL
  const params = new URLSearchParams();
  if (sidebarDefault) params.set('sidebar-default', sidebarDefault);
  const queryString = params.toString();
  const baseSdkUrl = buildSdkUrl(baseUrl, 'bots', botId, mode);
  const scriptUrl = queryString ? `${baseSdkUrl}?${queryString}` : baseSdkUrl;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    
    // Add data attributes for SDK configuration
    script.setAttribute('data-besper-bot-id', botId);
    script.setAttribute('data-besper-mode', mode);
    
    if (containerId) {
      script.setAttribute('data-container', containerId);
    }
    
    if (apiEndpoint) {
      script.setAttribute('data-api-endpoint', normalizeBaseUrlForSdk(apiEndpoint));
    } else if (baseUrl) {
      script.setAttribute('data-api-endpoint', normalizeBaseUrlForSdk(baseUrl));
    }

    const timeout = setTimeout(() => {
      reject(new Error(`Timeout loading SDK ${mode} script from ${scriptUrl} after 10 seconds`));
    }, 10000);

    script.onerror = (error) => {
      clearTimeout(timeout);
      logger.error('SDK-LOADER', `Failed to load SDK ${mode} script`, { error: error.message, scriptUrl });
      reject(new Error(`Failed to load SDK ${mode} script from ${scriptUrl}`));
    };

    script.onload = () => {
      clearTimeout(timeout);
      logger.debug('SDK-LOADER', `SDK ${mode} script loaded, waiting for ${customElementName} registration`);
      
      // Wait for custom element registration
      let attempts = 0;
      const checkInterval = setInterval(() => {
        if (customElements.get(customElementName)) {
          clearInterval(checkInterval);
          logger.debug('SDK-LOADER', `${customElementName} registered`);
          resolve();
        } else if (attempts++ >= 100) { // 10 seconds
          clearInterval(checkInterval);
          logger.error('SDK-LOADER', `${customElementName} not registered after script load`, {
            sdkConfig: window.__BESPER_SDK_CONFIG__ ? 'present' : 'missing'
          });
          reject(new Error(`Script loaded but ${customElementName} not registered after 10 seconds`));
        }
      }, 100);
    };

    // Append to body (same as implementation modal)
    document.body.appendChild(script);
  });
};

